import { useNavigate } from 'react-router-dom';
import { ETAPES } from './etapes';
import './Avancement.css';

/**
 * Avancement d'une demande, sur les six étapes numérotées du parcours
 * (cf. etapes.js — mêmes numéros que la sidebar).
 *
 * Aucun champ ne porte cette progression : elle se déduit de signaux répartis
 * sur quatre tables (devis, contrats, documents_session, contrat_sessions),
 * que la liste des demandes remonte avec chaque ligne.
 *
 * Limite connue : l'étape Rappel n'est journalisée nulle part. Elle est
 * déduite du démarrage de la session, date à laquelle le rappel a
 * nécessairement dû partir.
 */

const STATUTS_ABANDON = ['sans_suite', 'devis_refuse'];

/**
 * Les étapes qui se suivent sur une autre page. L'étape 1 en est absente :
 * c'est la liste des demandes elle-même, celle où ces boutons s'affichent.
 */
const ETAPES_LIEES = ETAPES.filter((e) => e.numero > 1);

/** Minuit du jour courant : les dates de session sont des dates, pas des instants. */
function aujourdhui() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function versDate(valeur) {
  if (!valeur) return null;
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Numéro de l'étape la plus avancée atteinte, ou 0 pour un dossier abandonné
 * (sans suite, devis refusé) : la progression s'arrête, on ne prétend pas
 * qu'elle continue.
 *
 * L'évaluation part de la fin : on retient la première condition satisfaite,
 * ce qui rend la progression monotone. Une relance (étape 2) sur un devis
 * déjà envoyé ne fait donc pas reculer la barre à 2 — le dossier reste à 3,
 * l'étape 2 étant simplement marquée franchie.
 */
export function numeroAvancement(contact) {
  if (STATUTS_ABANDON.includes(contact.dernier_statut)) return 0;

  const debut = versDate(contact.premiere_journee_session);
  const fin = versDate(contact.derniere_journee_session);
  const jour = aujourdhui();

  if (
    contact.dernier_statut === 'termine' ||
    contact.dernier_statut === 'feuillepresence_envoye' ||
    contact.dernier_statut === 'deja_conclu' ||
    (fin && fin < jour)
  ) {
    return ETAPES.length;
  }
  if (debut && debut <= jour) return 6; // la session a démarré : le rappel a dû partir
  if (Number(contact.nb_convocations) > 0) return 4;
  if (contact.contrat_id) return 3;
  // Un devis généré met le dossier SUR l'étape Devis : le contrat n'existe pas
  // encore, la barre ne doit pas déjà pointer vers lui.
  if (contact.devis_id) return 2;
  return 1;
}

/**
 * Barre d'avancement pleine largeur, posée sur sa propre ligne sous la
 * demande concernée : les six étapes ont besoin de toute la largeur du
 * tableau pour être nommées.
 *
 * Le pourcentage compte les étapes atteintes sur le total (1/6 dès la
 * réception, 6/6 une fois la formation terminée) plutôt que les intervalles
 * franchis : une demande tout juste reçue est déjà entrée dans le parcours,
 * afficher 0 % la ferait passer pour inexistante.
 */
function Avancement({ contact }) {
  const navigate = useNavigate();
  const numero = numeroAvancement(contact);
  const abandonne = numero === 0;
  const termine = contact.dernier_statut === 'termine';
  const pourcentage = abandonne ? 0 : Math.round((numero / ETAPES.length) * 100);
  // La barre s'arrête sous le point de l'étape courante (centre de sa
  // colonne), pas à la frontière suivante : sinon elle déborde déjà sur
  // l'étape d'après. Parcours terminé : pleine.
  const remplissage = abandonne
    ? 0
    : termine || numero >= ETAPES.length
      ? 100
      : ((numero - 0.5) / ETAPES.length) * 100;
  const courante = ETAPES.find((e) => e.numero === numero);

  /**
   * La liste de l'étape, restreinte à ce dossier : `contact` la filtre,
   * `client` nomme le dossier dans son bandeau. Les lignes concernées y
   * ressortent en vert, et le restent quand on lève le filtre.
   */
  function ouvrirEtape(etape) {
    const params = new URLSearchParams({
      contact: String(contact.id),
      client: contact.societe_nom || '',
    });
    if (etape.vue) params.set('vue', etape.vue);
    navigate(`${etape.route}?${params.toString()}`);
  }

  return (
    <div className={'avancement' + (abandonne ? ' avancement-abandon' : '')}>
      {/* Accès direct à l'étape, au-dessus de la progression : les étapes
          déjà franchies sont en vert, les autres restent atteignables — la
          liste dira simplement qu'il n'y a rien encore. */}
      <div className="avancement-acces">
        {ETAPES_LIEES.map((etape) => (
          <button
            key={etape.cle}
            type="button"
            className={
              'avancement-acces-bouton' +
              (!abandonne && (termine || etape.numero <= numero)
                ? ' avancement-acces-atteint'
                : '')
            }
            title={`Voir « ${etape.label} » pour ${contact.societe_nom || 'cette demande'}`}
            onClick={(e) => {
              e.stopPropagation();
              ouvrirEtape(etape);
            }}
          >
            {etape.court}
          </button>
        ))}
      </div>

      <div className="avancement-piste">
        <div className="avancement-remplissage" style={{ width: `${remplissage}%` }} />
      </div>

      <div className="avancement-etapes">
        {ETAPES.map((etape) => (
          <div
            key={etape.cle}
            title={`Étape ${etape.numero} — ${etape.label}`}
            className={
              'avancement-etape' +
              (abandonne
                ? ''
                : termine || etape.numero < numero
                  ? ' avancement-etape-faite'
                  : etape.numero === numero
                    ? ' avancement-etape-courante'
                    : '')
            }
          >
            <span className="avancement-point" aria-hidden="true">
              {etape.numero}
            </span>
            <span className="avancement-label">{etape.court}</span>
          </div>
        ))}
      </div>

      <div className="avancement-resume">
        {abandonne
          ? contact.dernier_statut === 'devis_refuse'
            ? 'Devis refusé — parcours interrompu'
            : 'Sans suite — parcours interrompu'
          : termine
            ? `Parcours terminé · ${ETAPES.length}/${ETAPES.length} · 100 %`
            : `Étape ${numero}/${ETAPES.length} · ${courante?.label ?? '—'} · ${pourcentage} %`}
      </div>
    </div>
  );
}

export default Avancement;
